import { t } from "i18next";

import { Box, Grid, Typography } from "@mui/material";

import VehicleStartAuctionCardContent from "@/src/components/molecules/VehicleStartAuctionCard";

import {
  instructionsBulletSx,
  instructionsContainerSx,
  instructionsContentSx,
  instructionsDesktopCtaSectionSx,
  instructionsDesktopCtaTitleSx,
  instructionsDividerSx,
  instructionsListItemTextSx,
  instructionsListSx,
  instructionsMobileContentSx,
  instructionsMobileSectionSx,
  instructionsMobileTitleSx,
  instructionsRootSx,
  instructionsTitleSx,
  MOBILE_GRID_ITEM_SX
} from "@/src/pages/consumer/HomePage/components/HomePageInstructions/homePageInstructions.styles";
import { CONSUMER_HOMEPAGE_PREFIX } from "@/src/pages/consumer/HomePage/homePage.constants";

import AB_TEST_IDS from "@/src/constants/abTestIds";
import { VehicleType } from "@/src/types/types";

interface HomePageInstructionsProps {
  carOnAuctionListingClick: () => void;
  motoOnAuctionListingClick: () => void;
  caravanOnAuctionListingClick: () => void;
  vehicleType: VehicleType;
}

export const HomePageInstructions = (props: HomePageInstructionsProps) => {
  const {
    carOnAuctionListingClick,
    motoOnAuctionListingClick,
    caravanOnAuctionListingClick,
    vehicleType
  } = props;
  const listItemKeys = Array.from({ length: 3 }, (_, i) => i + 1);

  return (
    <Box sx={instructionsRootSx}>
      <Box sx={instructionsContainerSx}>
        <Box sx={instructionsContentSx}>
          <Box>
            <Typography variant="h2" sx={instructionsTitleSx}>
              {t(`${CONSUMER_HOMEPAGE_PREFIX}.title2`)}
            </Typography>
            <Box sx={instructionsListSx}>
              <ul>
                {listItemKeys.map((item) => (
                  <li key={`instructionListItem-${item}`}>
                    <Box
                      sx={{
                        ...instructionsBulletSx,
                        backgroundImage: `url('/assets/images/instructions-bullet-${item}.webp')`
                      }}
                    />
                    <Box sx={instructionsListItemTextSx}>
                      <Typography variant="h3">
                        {t(
                          `${CONSUMER_HOMEPAGE_PREFIX}.${vehicleType}.instructionListItem-${item}-title`
                        )}
                      </Typography>
                      <Typography>
                        {t(
                          `${CONSUMER_HOMEPAGE_PREFIX}.${vehicleType}.instructionListItem-${item}-body`
                        )}
                      </Typography>
                    </Box>
                  </li>
                ))}
              </ul>
            </Box>
          </Box>

          <Box sx={instructionsDividerSx} />
          <Box sx={instructionsDesktopCtaSectionSx}>
            <Typography variant="h2" sx={instructionsDesktopCtaTitleSx}>
              {t(`${CONSUMER_HOMEPAGE_PREFIX}.${vehicleType}.sellCarTitle`)}
            </Typography>
            <Grid container spacing={3}>
              <Grid
                sx={{ mb: 1 }}
                size={{
                  xs: 12,
                  sm: 12
                }}
              >
                <VehicleStartAuctionCardContent
                  label={t(`${CONSUMER_HOMEPAGE_PREFIX}.cars`)}
                  backgroundUrl={["url('/assets/images/frontpage_car.webp')"]}
                  onAuctionListingClick={carOnAuctionListingClick}
                  imageSize="large"
                  imageRightOffset={-70}
                  imageTopOffset={10}
                  abTestId={AB_TEST_IDS.consumer.homepage.instructions.carCta}
                />
              </Grid>

              <Grid
                sx={{ mb: 2 }}
                size={{
                  xs: 12,
                  sm: 12
                }}
              >
                <VehicleStartAuctionCardContent
                  label={t(`${CONSUMER_HOMEPAGE_PREFIX}.motoVehicleTypes`)}
                  backgroundUrl={["url('/assets/images/red_motorbike.webp')"]}
                  onAuctionListingClick={motoOnAuctionListingClick}
                  imageSize="large"
                  abTestId={AB_TEST_IDS.consumer.homepage.instructions.motoCta}
                />
              </Grid>

              <Grid
                sx={{ mb: 2 }}
                size={{
                  xs: 12,
                  sm: 12
                }}
              >
                <VehicleStartAuctionCardContent
                  label={t(`${CONSUMER_HOMEPAGE_PREFIX}.caravanVehicleTypes`)}
                  backgroundUrl={[
                    "url('/assets/images/frontpage_caravan.webp')"
                  ]}
                  onAuctionListingClick={caravanOnAuctionListingClick}
                  imageSize="large"
                  imageRightOffset={-80}
                  abTestId={
                    AB_TEST_IDS.consumer.homepage.instructions.caravanCta
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
      <Box sx={instructionsMobileSectionSx}>
        <Box sx={instructionsMobileContentSx}>
          <Typography variant="h2" sx={instructionsMobileTitleSx}>
            {t(`${CONSUMER_HOMEPAGE_PREFIX}.${vehicleType}.sellCarTitle`)}
          </Typography>

          <Grid container spacing={3}>
            <Grid
              sx={MOBILE_GRID_ITEM_SX}
              size={{
                xs: 12,
                sm: 6
              }}
            >
              <VehicleStartAuctionCardContent
                label={t(`${CONSUMER_HOMEPAGE_PREFIX}.cars`)}
                backgroundUrl={["url('/assets/images/frontpage_car.webp')"]}
                onAuctionListingClick={carOnAuctionListingClick}
                imageSize="large"
                imageRightOffset={-70}
                abTestId={AB_TEST_IDS.consumer.homepage.instructions.carCta}
              />
            </Grid>

            <Grid
              sx={MOBILE_GRID_ITEM_SX}
              size={{
                xs: 12,
                sm: 6
              }}
            >
              <VehicleStartAuctionCardContent
                label={t(`${CONSUMER_HOMEPAGE_PREFIX}.motoVehicleTypes`)}
                backgroundUrl={["url('/assets/images/red_motorbike.webp')"]}
                onAuctionListingClick={motoOnAuctionListingClick}
                imageSize="large"
                abTestId={AB_TEST_IDS.consumer.homepage.instructions.motoCta}
              />
            </Grid>

            <Grid
              sx={MOBILE_GRID_ITEM_SX}
              size={{
                xs: 12,
                sm: 6
              }}
            >
              <VehicleStartAuctionCardContent
                label={t(`${CONSUMER_HOMEPAGE_PREFIX}.caravanVehicleTypes`)}
                backgroundUrl={["url('/assets/images/frontpage_caravan.webp')"]}
                onAuctionListingClick={caravanOnAuctionListingClick}
                imageSize="large"
                imageRightOffset={-70}
                abTestId={AB_TEST_IDS.consumer.homepage.instructions.caravanCta}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
